import { VERIFY_ACCOUNT_STEP_ORDER } from '@identity/presentation/constants';
import { useAccountStatus } from '@identity/presentation/hooks/useAccountStatus';
import { gateEnforcementFor, nextStepFor } from '@identity/presentation/utils';

export type UseAccountVerificationGateParams = {
  /**
   * Whether to ask at all. The App layer passes `false` with no session and on the routes where the
   * dialog must not appear, so no request is made from `/signin`.
   */
  isEnabled: boolean;
};

export type AccountVerificationGateResult = {
  /** True only when the answer is known, the mode asks for the dialog and something is pending. */
  shouldPrompt: boolean;
  /** Whether the dialog carries a way out. True only under `soft`, and only meaningful if prompted. */
  isDismissible: boolean;
};

/**
 * Whether this account must be asked to verify, and how hard.
 *
 * It is a query and not a polling: it asks once after the login, because it waits for no external
 * event. It derives the answer the same way the wizard does, so the two can never disagree, and over
 * the same cache key, so entering the wizard reuses this entry.
 *
 * It fails open always — while loading, on a network error, and for a mode it does not recognise.
 * The opposite failure locks the user base out of the application, and a dialog that appears one
 * login later costs nothing next to that.
 */
export const useAccountVerificationGate = ({
  isEnabled,
}: UseAccountVerificationGateParams): AccountVerificationGateResult => {
  const { data } = useAccountStatus({ enabled: isEnabled });

  const enforcement = gateEnforcementFor(data?.enforcementMode);

  if (!data || enforcement === 'none') return { shouldPrompt: false, isDismissible: false };

  return {
    shouldPrompt: nextStepFor(VERIFY_ACCOUNT_STEP_ORDER, data.requirements) !== 'DONE',
    isDismissible: enforcement === 'dismissible',
  };
};
